import { TourTip } from '@/components/TourTip';
/**
 * ============================================================
 * KEJJA OS — src/pages/finance/FundsPage.tsx  (v1.1)
 * Sinking funds — savings goals (school fees, plot, SACCO)
 * ============================================================
 */

import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Plus, Trash2, Target, Minus } from 'lucide-react';

import { useFinanceContext } from './FinanceLayout';
import {
  useFinanceFunds, useCreateFund, useDeleteFund, useContributeToFund,
} from '@/hooks/useFinance';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoneyCompact } from '@/lib/formatMoney';
import { cn } from '@/lib/utils';

const PRIORITY_LABELS = ['Low', 'Medium', 'High'];
const PRIORITY_COLORS = ['bg-slate-200 text-slate-700', 'bg-amber-100 text-amber-700', 'bg-rose-100 text-rose-700'];

const PALETTE = ['#f97316', '#0891b2', '#10b981', '#8b5cf6', '#ec4899', '#eab308'];

export default function FundsPage() {
  const { homeId } = useFinanceContext();
  const { data: funds, isLoading } = useFinanceFunds(homeId);
  const createFund = useCreateFund(homeId);
  const deleteFund = useDeleteFund(homeId);
  const contribute = useContributeToFund(homeId);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    name: '', targetAmount: '', savedAmount: '',
    targetDate: '', priority: 1, color: PALETTE[0],
  });
  const [contribFor, setContribFor] = useState<string | null>(null);
  const [contribAmount, setContribAmount] = useState('');

  function handleAdd(e: FormEvent) {
    e.preventDefault();
    const targetMinor = Math.round(parseFloat(form.targetAmount) * 100);
    const savedMinor = form.savedAmount ? Math.round(parseFloat(form.savedAmount) * 100) : 0;
    if (!form.name.trim() || !targetMinor) return;

    createFund.mutate({
      name: form.name.trim(),
      targetAmount: targetMinor,
      savedAmount: savedMinor,
      targetDate: form.targetDate || null,
      priority: form.priority,
      color: form.color,
    }, {
      onSuccess: () => {
        setShowAdd(false);
        setForm({ name: '', targetAmount: '', savedAmount: '', targetDate: '', priority: 1, color: PALETTE[0] });
      },
    });
  }

  function handleContribute(fundId: string, direction: 'in' | 'out') {
    const minor = Math.round(parseFloat(contribAmount) * 100);
    if (!minor) return;
    contribute.mutate(
      { fundId, delta: direction === 'in' ? minor : -minor },
      {
        onSuccess: () => { setContribFor(null); setContribAmount(''); },
      },
    );
  }

  return (
    <div className="space-y-5 pb-12">
      
      <TourTip
        id="finance-v1"
        title={"Money in one place"}
        body={"Track income + spending across accounts, Sacco, Fuliza, and bills."}
        bullets={["Forward any M-Pesa SMS → paste into the M-Pesa tab", "Budgets alert you when you overspend", "Bills remind you 3 days before they're due"]}
        accent="amber"
      />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Saving for</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Goals with target amounts — school fees, plot deposit, SACCO targets
          </p>
        </div>
        <Button
          size="sm"
          className="bg-orange-600 hover:bg-orange-700 text-white"
          onClick={() => setShowAdd(!showAdd)}
        >
          <Plus className="w-4 h-4 mr-1" /> New goal
        </Button>
      </div>

      {showAdd && (
        <Card className="border-orange-200 bg-orange-50/30 dark:bg-orange-950/10">
          <CardContent className="p-4">
            <form onSubmit={handleAdd} className="space-y-3">
              <div>
                <Label htmlFor="fund-name" className="text-xs">Goal name</Label>
                <Input
                  id="fund-name"
                  placeholder="e.g. Form 2 Fees Term 2, Plot Deposit"
                  value={form.name}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="fund-target" className="text-xs">Target (KSh)</Label>
                  <Input
                    id="fund-target" type="number" step="0.01"
                    value={form.targetAmount}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, targetAmount: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="fund-saved" className="text-xs">Already saved</Label>
                  <Input
                    id="fund-saved" type="number" step="0.01"
                    placeholder="0"
                    value={form.savedAmount}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, savedAmount: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="fund-date" className="text-xs">Target date (optional)</Label>
                <Input
                  id="fund-date" type="date"
                  value={form.targetDate}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, targetDate: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs block mb-1.5">Priority</Label>
                <div className="grid grid-cols-3 gap-1">
                  {PRIORITY_LABELS.map((lbl, i) => (
                    <button
                      key={i} type="button"
                      onClick={() => setForm({ ...form, priority: i })}
                      className={cn(
                        'px-3 py-1.5 text-xs rounded-lg border font-medium',
                        form.priority === i
                          ? 'bg-orange-600 text-white border-orange-600'
                          : 'border-border bg-background hover:bg-muted',
                      )}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs block mb-1.5">Color</Label>
                <div className="flex gap-2">
                  {PALETTE.map((c) => (
                    <button
                      key={c} type="button"
                      onClick={() => setForm({ ...form, color: c })}
                      className={cn(
                        'w-7 h-7 rounded-full border-2 transition-all',
                        form.color === c ? 'border-foreground scale-110' : 'border-transparent',
                      )}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button type="submit" disabled={createFund.isPending} className="bg-orange-600 hover:bg-orange-700 text-white">
                  {createFund.isPending ? 'Creating…' : 'Create goal'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
      ) : !funds || funds.funds.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Target className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              No goals yet. Start with the big ones: school fees, SACCO target, plot deposit.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {funds.funds.map((f) => {
            const percent = f.targetAmount > 0 ? Math.min(100, Math.round((f.savedAmount / f.targetAmount) * 100)) : 0;
            const remaining = Math.max(0, f.targetAmount - f.savedAmount);
            const targetDateObj = f.targetDate ? new Date(f.targetDate + 'T00:00:00Z') : null;
            const daysToGo = targetDateObj
              ? Math.ceil((targetDateObj.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
              : null;

            return (
              <Card key={f.id} className="overflow-hidden">
                <div className="h-1.5" style={{ backgroundColor: f.color ?? '#f97316' }} />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-sm">{f.name}</h3>
                        {f.isAchieved && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[9px]">achieved</Badge>
                        )}
                        <Badge className={cn('text-[9px]', PRIORITY_COLORS[f.priority])}>
                          {PRIORITY_LABELS[f.priority]}
                        </Badge>
                      </div>
                      {daysToGo !== null && (
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {daysToGo < 0 ? `${Math.abs(daysToGo)}d overdue` : daysToGo === 0 ? 'Due today' : `${daysToGo} days to go`}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-600 flex-shrink-0"
                      onClick={() => { if (confirm(`Delete "${f.name}"?`)) deleteFund.mutate(f.id); }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>

                  <div className="mt-4">
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-xl font-bold tabular-nums">{formatMoneyCompact(f.savedAmount, f.currencyCode)}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        of {formatMoneyCompact(f.targetAmount, f.currencyCode)}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${percent}%`, backgroundColor: f.color ?? '#f97316' }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[10px] text-muted-foreground tabular-nums">{percent}%</span>
                      {remaining > 0 && (
                        <span className="text-[10px] text-muted-foreground tabular-nums">
                          {formatMoneyCompact(remaining, f.currencyCode)} to go
                        </span>
                      )}
                    </div>
                  </div>

                  {contribFor === f.id ? (
                    <div className="mt-3 flex gap-1.5 items-center">
                      <Input
                        type="number" step="0.01" placeholder="KSh"
                        value={contribAmount}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setContribAmount(e.target.value)}
                        className="h-8 text-sm"
                        autoFocus
                      />
                      <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleContribute(f.id, 'in')} disabled={contribute.isPending}>
                        <Plus className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => handleContribute(f.id, 'out')} disabled={contribute.isPending}>
                        <Minus className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setContribFor(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <Button
                      size="sm" variant="outline" className="w-full mt-3 h-8 text-xs"
                      onClick={() => { setContribFor(f.id); setContribAmount(''); }}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Contribute / Withdraw
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
